import { supabase } from '../supabase';

export interface WeatherData {
  location: string;
  city: string;
  state: string;
  temperature: number;
  feels_like: number;
  temp_min: number;
  temp_max: number;
  humidity: number;
  pressure: number;
  description: string;
  icon: string;
  wind_speed: number;
  clouds: number;
  timestamp: string;
}

interface OpenWeatherResponse {
  main: {
    temp: number;
    feels_like: number;
    temp_min: number;
    temp_max: number;
    pressure: number;
    humidity: number;
  };
  weather: Array<{
    description: string;
    icon: string;
  }>;
  wind: {
    speed: number;
  };
  clouds: {
    all: number;
  };
  name: string;
}

const LOCATIONS = [
  { city: 'St Petersburg', state: 'FL', lat: 27.7676, lon: -82.6403 },
  { city: 'Boynton Beach', state: 'FL', lat: 26.5253, lon: -80.0664 },
  { city: 'Oakland Park', state: 'FL', lat: 26.1723, lon: -80.1320 }
];

const CACHE_TTL_MINUTES = 30;
const API_KEY = import.meta.env.VITE_OPENWEATHER_API_KEY;

export class WeatherRepository {
  /**
   * Fetch weather for all 3 locations with cache-first strategy
   */
  static async fetchWeather(): Promise<WeatherData[]> {
    if (!API_KEY) {
      console.error('OpenWeatherMap API key not configured');
      return [];
    }

    const results: WeatherData[] = [];

    for (const location of LOCATIONS) {
      try {
        // Check cache first
        const cached = await this.getCachedWeather(location.city, location.state);
        
        if (cached) {
          results.push(cached);
          continue;
        }

        // Fetch fresh data from API
        const fresh = await this.fetchFromAPI(location);
        
        if (fresh) {
          // Cache the result
          await this.cacheWeather(fresh);
          results.push(fresh);
        }
      } catch (error) {
        console.error(`Error fetching weather for ${location.city}:`, error);
        // Try to get stale cache as fallback
        const stale = await this.getStaleCache(location.city, location.state);
        if (stale) {
          results.push(stale);
        }
      }
    }

    return results;
  }

  /**
   * Get cached weather if it's fresh (within TTL)
   */
  private static async getCachedWeather(
    city: string,
    state: string
  ): Promise<WeatherData | null> {
    const cutoff = new Date();
    cutoff.setMinutes(cutoff.getMinutes() - CACHE_TTL_MINUTES);

    const { data, error } = await supabase
      .from('weather_cache')
      .select('*')
      .eq('city', city)
      .eq('state', state)
      .gte('cached_at', cutoff.toISOString())
      .order('cached_at', { ascending: false })
      .limit(1)
      .single();

    if (error || !data) {
      return null;
    }

    return {
      location: `${data.city}, ${data.state}`,
      city: data.city,
      state: data.state,
      temperature: data.temperature,
      feels_like: data.feels_like,
      temp_min: data.temp_min,
      temp_max: data.temp_max,
      humidity: data.humidity,
      pressure: data.pressure,
      description: data.description,
      icon: data.icon,
      wind_speed: data.wind_speed,
      clouds: data.clouds,
      timestamp: data.cached_at
    };
  }

  /**
   * Get stale cache data as fallback (ignoring TTL)
   */
  private static async getStaleCache(
    city: string,
    state: string
  ): Promise<WeatherData | null> {
    const { data, error } = await supabase
      .from('weather_cache')
      .select('*')
      .eq('city', city)
      .eq('state', state)
      .order('cached_at', { ascending: false })
      .limit(1)
      .single();

    if (error || !data) {
      return null;
    }

    return {
      location: `${data.city}, ${data.state}`,
      city: data.city,
      state: data.state,
      temperature: data.temperature,
      feels_like: data.feels_like,
      temp_min: data.temp_min,
      temp_max: data.temp_max,
      humidity: data.humidity,
      pressure: data.pressure,
      description: data.description,
      icon: data.icon,
      wind_speed: data.wind_speed,
      clouds: data.clouds,
      timestamp: data.cached_at
    };
  }

  /**
   * Fetch weather from OpenWeatherMap API
   */
  private static async fetchFromAPI(location: {
    city: string;
    state: string;
    lat: number;
    lon: number;
  }): Promise<WeatherData | null> {
    const url = `https://api.openweathermap.org/data/2.5/weather?lat=${location.lat}&lon=${location.lon}&appid=${API_KEY}&units=imperial`;

    const response = await fetch(url);
    
    if (!response.ok) {
      throw new Error(`Weather API error: ${response.status} ${response.statusText}`);
    }

    const data: OpenWeatherResponse = await response.json();

    return {
      location: `${location.city}, ${location.state}`,
      city: location.city,
      state: location.state,
      temperature: Math.round(data.main.temp),
      feels_like: Math.round(data.main.feels_like),
      temp_min: Math.round(data.main.temp_min),
      temp_max: Math.round(data.main.temp_max),
      humidity: data.main.humidity,
      pressure: data.main.pressure,
      description: data.weather[0]?.description || 'Unknown',
      icon: data.weather[0]?.icon || '01d',
      wind_speed: Math.round(data.wind.speed),
      clouds: data.clouds.all,
      timestamp: new Date().toISOString()
    };
  }

  /**
   * Cache weather data in database
   */
  private static async cacheWeather(weather: WeatherData): Promise<void> {
    const { error } = await supabase
      .from('weather_cache')
      .insert({
        city: weather.city,
        state: weather.state,
        temperature: weather.temperature,
        feels_like: weather.feels_like,
        temp_min: weather.temp_min,
        temp_max: weather.temp_max,
        humidity: weather.humidity,
        pressure: weather.pressure,
        description: weather.description,
        icon: weather.icon,
        wind_speed: weather.wind_speed,
        clouds: weather.clouds,
        cached_at: weather.timestamp
      });

    if (error) {
      console.error('Error caching weather data:', error);
    }
  }

  /**
   * Get weather icon URL from OpenWeatherMap
   */
  static getIconUrl(icon: string): string {
    return `https://openweathermap.org/img/wn/${icon}@2x.png`;
  }

  /**
   * Format temperature for display
   */
  static formatTemp(temp: number): string {
    return `${temp}°F`;
  }

  /**
   * Get weather condition emoji based on icon code
   */
  static getWeatherEmoji(icon: string): string {
    const code = icon.substring(0, 2);
    const emojiMap: { [key: string]: string } = {
      '01': '☀️', // clear sky
      '02': '⛅', // few clouds
      '03': '☁️', // scattered clouds
      '04': '☁️', // broken clouds
      '09': '🌧️', // shower rain
      '10': '🌦️', // rain
      '11': '⛈️', // thunderstorm
      '13': '❄️', // snow
      '50': '🌫️'  // mist
    };
    return emojiMap[code] || '🌤️';
  }
}
